import React, { useEffect } from "react";
import { MapPin as MapPinIcon } from "lucide-react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  ZoomControl,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { OFFICES } from "../data/mock";

// Configurazione per-tipo delle icone (allineata a ProjectsMap)
const OFFICE_ICON_CONFIG = {
  commerciale: {
    fill: "#0A1F44",
    stroke: "#F4C542",
    inner:
      '<path d="M8 18 L15 12 L22 18 L22 24 L8 24 Z" fill="#0FB36B" stroke="#F4C542" stroke-width="1"/><rect x="13.5" y="19" width="3" height="5" fill="#0A1F44"/>',
  },
  magazzino: {
    fill: "#0FB36B",
    stroke: "#0A1F44",
    inner:
      '<rect x="8" y="13" width="14" height="11" fill="#F4C542" stroke="#0A1F44" stroke-width="1"/><path d="M8 13 L15 9 L22 13 Z" fill="#0A1F44"/><rect x="13.5" y="18" width="3" height="6" fill="#0A1F44"/>',
  },
  legale: {
    fill: "#F4C542",
    stroke: "#0A1F44",
    inner:
      '<rect x="10" y="11" width="10" height="13" fill="#FFFFFF" stroke="#0A1F44" stroke-width="1"/><line x1="12" y1="15" x2="18" y2="15" stroke="#0A1F44" stroke-width="1"/><line x1="12" y1="18" x2="18" y2="18" stroke="#0A1F44" stroke-width="1"/><line x1="12" y1="21" x2="16" y2="21" stroke="#0A1F44" stroke-width="1"/>',
  },
};

const makeOfficeIcon = (kind) => {
  const cfg = OFFICE_ICON_CONFIG[kind] || OFFICE_ICON_CONFIG.commerciale;
  return L.divIcon({
    className: `energeide-pin energeide-pin--${kind}`,
    html: `
      <div style="position: relative; width: 34px; height: 44px;">
        <svg viewBox="0 0 30 40" width="34" height="44" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M15 0 C6.7 0 0 6.7 0 15 c0 11 15 25 15 25 s15-14 15-25 c0-8.3-6.7-15-15-15z"
            fill="${cfg.fill}"
            stroke="${cfg.stroke}"
            stroke-width="2"
          />
          ${cfg.inner}
        </svg>
      </div>
    `,
    iconSize: [34, 44],
    iconAnchor: [17, 44],
    popupAnchor: [0, -40],
  });
};

const officeIcons = OFFICES.reduce((acc, o) => {
  acc[o.id] = makeOfficeIcon(o.kind);
  return acc;
}, {});

const FitBoundsToOffices = () => {
  const map = useMap();
  useEffect(() => {
    const points = OFFICES.map((o) => [o.lat, o.lng]);
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 11, { animate: false });
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
  }, [map]);
  return null;
};

const LEGEND_LABEL = {
  commerciale: "Sede commerciale",
  magazzino: "Sede magazzino",
  legale: "Sede legale",
};

const OfficesMap = () => {
  return (
    <div
      data-testid="offices-map-container"
      className="relative isolate w-full rounded-2xl overflow-hidden shadow-md border border-gray-100 bg-white"
      style={{ height: "360px" }}
    >
      <MapContainer
        center={[42.5, 12.5]}
        zoom={6}
        minZoom={5}
        maxZoom={16}
        scrollWheelZoom={false}
        zoomControl={false}
        style={{ width: "100%", height: "100%" }}
      >
        <FitBoundsToOffices />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ZoomControl position="topright" />
        {OFFICES.map((o) => (
          <Marker
            key={o.id}
            position={[o.lat, o.lng]}
            icon={officeIcons[o.id]}
          >
            <Popup>
              <div className="font-montserrat min-w-[200px] max-w-[240px]">
                <p className="text-sm font-bold text-[#0A1F44] leading-snug mb-1">
                  {o.title} Energeide
                </p>
                <p className="flex items-start gap-1.5 text-xs text-gray-600 mb-1">
                  <MapPinIcon className="w-3 h-3 text-[#0FB36B] mt-0.5 shrink-0" />
                  <span>
                    {o.line1}
                    <br />
                    {o.line2}
                  </span>
                </p>
                {o.note ? (
                  <span className="inline-block text-[11px] font-semibold text-[#0A1F44] bg-[#F4C542]/25 px-2 py-0.5 rounded">
                    {o.note}
                  </span>
                ) : null}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <div className="absolute bottom-3 left-3 z-[400] bg-white/95 backdrop-blur-sm rounded-lg shadow-md px-3 py-2 flex flex-col gap-1.5 text-xs font-montserrat">
        {OFFICES.map((o) => {
          const cfg = OFFICE_ICON_CONFIG[o.kind] || OFFICE_ICON_CONFIG.commerciale;
          return (
            <div key={o.id} className="flex items-center gap-2">
              <span
                className="w-2.5 h-2.5 rounded-full border-2"
                style={{ backgroundColor: cfg.fill, borderColor: cfg.stroke }}
              />
              <span className="text-[#0A1F44] font-semibold">
                {LEGEND_LABEL[o.kind] || o.title}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OfficesMap;
